import { useNavigate } from "react-router-dom";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, CheckCircle2, Lock, Coins, TrendingUp, Rocket } from "lucide-react";
import { getInstructorCategory } from "@/lib/instructorMapping";
import { isCourseAccessible } from "@/lib/curriculumPhases";
import { cn } from "@/lib/utils";
import { useAccessTier } from "@/hooks/useAccessTier";

const CATEGORY_ICON = {
  finance: Coins,
  economics: TrendingUp,
  entrepreneurship: Rocket,
};

const CATEGORY_RING: Record<string, string> = {
  finance: "from-primary/40 to-primary/0",
  economics: "from-accent/40 to-accent/0",
  entrepreneurship: "from-secondary/40 to-secondary/0",
};

export default function ContinueLearningRow() {
  const navigate = useNavigate();
  const { courseProgress, loading } = useCurriculumProgress();
  const { tier } = useAccessTier();
  const beta = false; // paid product: no restricted tier

  if (beta) return null;

  if (loading) {
    return (
      <section className="space-y-3">
        <h2 className="font-heading font-bold text-lg text-foreground">Continue Learning</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  // Prioritize: in-progress (started, not complete), then not started accessible, limit to 6
  const accessible = courseProgress.filter((c) => isCourseAccessible(c.courseName));
  const inProgress = accessible.filter((c) => c.completedLessons > 0 && !c.isComplete);
  const notStarted = accessible.filter((c) => c.completedLessons === 0);
  const completed = accessible.filter((c) => c.isComplete);

  const display = [...inProgress, ...notStarted, ...completed].slice(0, 6);

  if (display.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="font-heading font-bold text-lg text-foreground">Continue Learning</h2>
        <div className="rounded-2xl border border-border/40 bg-card/40 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Start your first lesson to unlock progress tracking.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg text-foreground">Continue Learning</h2>
        <button
          onClick={() => navigate("/curriculum")}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          View all <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {display.map((course) => {
          const category = getInstructorCategory(course.courseName);
          const Icon = CATEGORY_ICON[category];
          const pct =
            course.totalLessons > 0
              ? Math.round((course.completedLessons / course.totalLessons) * 100)
              : 0;
          const status = course.isComplete
            ? "Complete"
            : course.completedLessons > 0
            ? "In Progress"
            : "Not Started";

          return (
            <button
              key={course.courseName}
              onClick={() => navigate(`/course/${encodeURIComponent(course.courseName)}`)}
              className="group relative text-left rounded-2xl border border-border/60 bg-card/80 backdrop-blur-md p-4 hover:border-primary/50 hover:bg-card transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_hsl(var(--primary)/0.4)]"
            >
              {/* Top: icon + status */}
              <div className="flex items-start justify-between mb-3">
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br border border-border/40",
                  CATEGORY_RING[category]
                )}>
                  <Icon className="w-5 h-5 text-foreground" />
                </div>
                <span className={cn(
                  "text-[10px] font-heading font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border",
                  course.isComplete
                    ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
                    : course.completedLessons > 0
                    ? "text-primary border-primary/40 bg-primary/10"
                    : "text-muted-foreground border-border/40 bg-muted/20"
                )}>
                  {status}
                </span>
              </div>

              <p className="font-heading font-bold text-sm text-foreground leading-snug line-clamp-2 min-h-[2.5rem]">
                {course.courseName}
              </p>
              <p className="text-[11px] text-foreground/70 mt-1 font-medium">
                {course.completedLessons}/{course.totalLessons} lessons
              </p>

              {/* Progress bar */}
              <div className="mt-3 h-2 w-full rounded-full bg-muted/70 overflow-hidden border border-border/40">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary via-secondary to-accent transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-foreground/70 font-semibold">{pct}%</span>
                {course.isComplete && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}